/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import OrderService from '../services/OrderService'
import clsx from 'clsx'

const OrderDetails = () => {
    const { currentUser } = useContext(AppContext)
    const { orderId } = useParams()
    const history = useHistory()
    
    const [order, setOrder] = useState(null)
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [showStatusModal, setShowStatusModal] = useState(false)
    const [newStatus, setNewStatus] = useState('')
    const [statusRemarks, setStatusRemarks] = useState('')

    useEffect(() => {
        if (orderId) {
            fetchOrderDetails()
        }
    }, [orderId])

    const fetchOrderDetails = async () => {
        setLoading(true)
        try {
            const response = await OrderService.getOrderDetails(orderId)
            
            if (response.success) {
                setOrder(response.data)
            } else {
                console.error('Failed to fetch order details:', response.errorMessage || response.message)
                alert('Failed to load order details')
                history.push('/sales-orders')
            }
        } catch (error) {
            console.error('Error fetching order details:', error)
            alert('Error loading order details')
            history.push('/sales-orders')
        } finally {
            setLoading(false)
        }
    }

    const getStatusBadge = (status) => {
        const statusInfo = OrderService.formatOrderStatus(status)
        return `badge-${statusInfo.color}`
    }

    const calculateSubtotal = () => {
        const items = order.order_item || order.orderItem || []
        return items.reduce((total, item) => {
            return total + (parseFloat(item.price || 0) * parseFloat(item.qtyOrdered || 0))
        }, 0)
    }

    const handlePrintOrder = async () => {
        try {
            await OrderService.printOrder(orderId)
        } catch (error) {
            console.error('Error printing order:', error)
            alert('Error printing order. Please try again.')
        }
    }

    const openStatusModal = () => {
        setNewStatus(order.status)
        setStatusRemarks('')
        setShowStatusModal(true)
    }

    const closeStatusModal = () => {
        setShowStatusModal(false)
        setNewStatus('')
        setStatusRemarks('')
    }

    const handleStatusChange = async () => {
        if (!newStatus) {
            alert('Please select a status')
            return
        }

        setActionLoading(true)
        
        try {
            const response = await OrderService.changeOrderStatus({
                orderId: order.orderId,
                orderStatus: newStatus,
                remarks: statusRemarks
            })

            if (response.success) {
                setOrder({ ...order, status: newStatus })
                closeStatusModal()
                alert('Order status updated successfully!')
            } else {
                alert('Failed to update order status: ' + (response.errorMessage || response.message))
            }
        } catch (error) {
            console.error('Error updating order status:', error)
            alert('Error updating order status. Please try again.')
        } finally {
            setActionLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading order details...</p>
                </div>
            </div>
        )
    }

    if (!order) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <h4>Order not found</h4>
                    <Link to="/sales-orders" className="btn btn-primary">
                        Back to Orders
                    </Link>
                </div>
            </div>
        )
    }

    return (
        <div className="container-fluid">
            {/* Page Header */}
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-4">
                <div className="mb-3 mb-lg-0">
                    <div className="d-flex align-items-center">
                        <Link to="/sales-orders" className="btn btn-link p-0 me-3">
                            <i className="fas fa-arrow-left"></i>
                        </Link>
                        <div>
                            <h1 className="h3 mb-0">Order Details</h1>
                            <p className="text-muted mb-0">Order #{order.orderNo}</p>
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <button
                        className="btn btn-outline-success btn-sm"
                        onClick={openStatusModal}
                    >
                        <i className="fas fa-edit me-1"></i>
                        <span className="d-none d-md-inline">Change Status</span>
                        <span className="d-md-none">Status</span>
                    </button>
                    <button
                        className="btn btn-outline-info btn-sm"
                        onClick={handlePrintOrder}
                    >
                        <i className="fas fa-print me-1"></i>
                        <span className="d-none d-md-inline">Print Order</span>
                        <span className="d-md-none">Print</span>
                    </button>
                    <Link
                        to={`/sales-invoices/create/${order.orderId}`}
                        className="btn btn-outline-warning btn-sm"
                    >
                        <i className="fas fa-file-invoice me-1"></i>
                        <span className="d-none d-md-inline">Create Invoice</span>
                        <span className="d-md-none">Invoice</span>
                    </Link>
                    <Link
                        to={`/shipments/create/${order.orderId}`}
                        className="btn btn-outline-primary btn-sm"
                    >
                        <i className="fas fa-shipping-fast me-1"></i>
                        <span className="d-none d-md-inline">Create Shipment</span>
                        <span className="d-md-none">Ship</span>
                    </Link>
                    <Link
                        to={`/payments/receive/create/${order.orderId}`}
                        className="btn btn-outline-success btn-sm"
                    >
                        <i className="fas fa-money-bill-wave me-1"></i>
                        <span className="d-none d-md-inline">Receive Payment</span>
                        <span className="d-md-none">Payment</span>
                    </Link>
                </div>
            </div>

            <div className="row">
                {/* Order Summary */}
                <div className="col-lg-8">
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Order Summary</h5>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-6 col-md-12 mb-3">
                                    <table className="table table-borderless">
                                        <tbody>
                                            <tr>
                                                <td><strong>Order ID:</strong></td>
                                                <td>#{order.orderId}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Order No:</strong></td>
                                                <td>{order.orderNo}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Order Date:</strong></td>
                                                <td>{OrderService.formatDate(order.createdAt)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Status:</strong></td>
                                                <td>
                                                    <span className={clsx('badge', getStatusBadge(order.status))}>
                                                        {OrderService.formatOrderStatus(order.status).label}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="col-lg-6 col-md-12 mb-3">
                                    <table className="table table-borderless">
                                        <tbody>
                                            <tr>
                                                <td><strong>Vendor:</strong></td>
                                                <td>{order.vendor?.name || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Customer Group:</strong></td>
                                                <td>{order.customer?.customerGroup?.customerCroupCode || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Payment Method:</strong></td>
                                                <td>{(order.payment_method || order.paymentMethod)?.name || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Shipping Method:</strong></td>
                                                <td>{(order.shipping_method || order.shippingMethod)?.methodName || 'N/A'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Customer Information */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Customer Information</h5>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-6 col-md-12 mb-3">
                                    <h6>Customer Details</h6>
                                    <p className="mb-1"><strong>{order.customer?.fullName || 'N/A'}</strong></p>
                                    <p className="mb-1">Email: {order.customer?.email || 'N/A'}</p>
                                    <p className="mb-1">Mobile: {order.customer?.mobileNo || 'N/A'}</p>
                                    <p className="mb-0">Gender: {order.customer?.gender || 'N/A'}</p>
                                </div>
                                <div className="col-lg-6 col-md-12 mb-3">
                                    <h6>Shipping Address</h6>
                                    {(order.sales_order_shipping_address || order.salesOrderShippingAddress) ? (
                                        <div className="address-details">
                                            <div className="address-line">
                                                <i className="fas fa-user text-muted me-2"></i>
                                                <strong>{(order.sales_order_shipping_address || order.salesOrderShippingAddress).fullName || 'N/A'}</strong>
                                            </div>
                                            <div className="address-line">
                                                <i className="fas fa-home text-muted me-2"></i>
                                                {((order.sales_order_shipping_address || order.salesOrderShippingAddress).house && `House: ${(order.sales_order_shipping_address || order.salesOrderShippingAddress).house}`) || ''}
                                                {((order.sales_order_shipping_address || order.salesOrderShippingAddress).floor_no && `, Floor: ${(order.sales_order_shipping_address || order.salesOrderShippingAddress).floor_no}`) || ''}
                                                {((order.sales_order_shipping_address || order.salesOrderShippingAddress).apartment_no && `, Flat: ${(order.sales_order_shipping_address || order.salesOrderShippingAddress).apartment_no}`) || ''}
                                            </div>
                                            <div className="address-line">
                                                <i className="fas fa-road text-muted me-2"></i>
                                                {(order.sales_order_shipping_address || order.salesOrderShippingAddress).street}
                                                {((order.sales_order_shipping_address || order.salesOrderShippingAddress).block && `, ${(order.sales_order_shipping_address || order.salesOrderShippingAddress).block}`) || ''}
                                            </div>
                                            <div className="address-line">
                                                <i className="fas fa-map-marked-alt text-muted me-2"></i>
                                                {(order.sales_order_shipping_address || order.salesOrderShippingAddress).area}, {(order.sales_order_shipping_address || order.salesOrderShippingAddress).region}
                                                {((order.sales_order_shipping_address || order.salesOrderShippingAddress).postcode && ` - ${(order.sales_order_shipping_address || order.salesOrderShippingAddress).postcode}`) || ''}
                                            </div>
                                            <div className="address-line">
                                                <i className="fas fa-phone text-muted me-2"></i>
                                                {(order.sales_order_shipping_address || order.salesOrderShippingAddress).phoneNumber}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <i className="fas fa-map-marker-alt text-muted fa-2x mb-2"></i>
                                            <p className="text-muted mb-0">No shipping address available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {order.prefferedDeliveryDate && (
                                <div className="row">
                                    <div className="col-12">
                                        <h6>Delivery Preferences</h6>
                                        <p className="mb-1">Date: {order.prefferedDeliveryDate}</p>
                                        {order.prefferedDeliveryFromTime && (
                                            <p className="mb-1">Time: {order.prefferedDeliveryFromTime} - {order.prefferedDeliveryToTime}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Order Items */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Order Items</h5>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th>Price</th>
                                            <th>Qty Ordered</th>
                                            <th>Qty Invoiced</th>
                                            <th>Qty Shipped</th>
                                            <th>Tax</th>
                                            <th>Discount</th>
                                            <th>Row Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(order.order_item || order.orderItem)?.map((item) => (
                                            <tr key={item.orderItemId}>
                                                <td>
                                                    <div>
                                                        <strong>{item.name}</strong>
                                                        {item.description && (
                                                            <div className="text-muted small">
                                                                {item.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{item.sku}</td>
                                                <td>{OrderService.formatCurrency(item.price)}</td>
                                                <td>{parseInt(item.qtyOrdered) || 0}</td>
                                                <td>{parseInt(item.qtyInvoiced) || 0}</td>
                                                <td>{parseInt(item.qtyShipped) || 0}</td>
                                                <td>{OrderService.formatCurrency(item.taxAmount)}</td>
                                                <td>{OrderService.formatCurrency(item.discountAmount)}</td>
                                                <td>{OrderService.formatCurrency(item.rowTotal)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Sidebar */}
                <div className="col-lg-4">
                    {/* Order Totals */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Order Totals</h5>
                        </div>
                        <div className="card-body">
                            <table className="table table-borderless">
                                <tbody>
                                    <tr>
                                        <td>Subtotal:</td>
                                        <td className="text-end">{OrderService.formatCurrency(calculateSubtotal())}</td>
                                    </tr>
                                    <tr>
                                        <td>Discount:</td>
                                        <td className="text-end text-danger">-{OrderService.formatCurrency(order.discountAmount || 0)}</td>
                                    </tr>
                                    {(order.promoAmount || 0) > 0 && (
                                        <tr>
                                            <td>Promo Discount:</td>
                                            <td className="text-end text-danger">-{OrderService.formatCurrency(order.promoAmount || 0)}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>Shipping:</td>
                                        <td className="text-end">{OrderService.formatCurrency(order.shippingAmount || 0)}</td>
                                    </tr>
                                    <tr>
                                        <td>Shipping Tax:</td>
                                        <td className="text-end">{OrderService.formatCurrency(order.shippingTaxAmount || 0)}</td>
                                    </tr>
                                    <tr className="border-top">
                                        <th>Grand Total:</th>
                                        <th className="text-end">{OrderService.formatCurrency(order.grandTotal || 0)}</th>
                                    </tr>
                                    <tr>
                                        <td>Total Paid:</td>
                                        <td className="text-end text-success">{OrderService.formatCurrency(order.totalPaid || 0)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Invoices */}
                    {order.invoices && order.invoices.length > 0 && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Invoices</h5>
                            </div>
                            <div className="card-body">
                                {order.invoices.map((invoice) => (
                                    <div key={invoice.entityId} className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <Link 
                                                to={`/sales-invoices/${invoice.entityId}`}
                                                className="text-decoration-none"
                                            >
                                                {invoice.invoiceNo}
                                            </Link>
                                            <div className="text-muted small">{invoice.invoiceDate}</div>
                                        </div>
                                        <div className="text-end">
                                            <div>{OrderService.formatCurrency(invoice.amount)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Shipments */}
                    {order.shipments && order.shipments.length > 0 && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Shipments</h5>
                            </div>
                            <div className="card-body">
                                {order.shipments.map((shipment) => (
                                    <div key={shipment.salesShipmentId} className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <div>{shipment.shipmentNo}</div>
                                            <div className="text-muted small">{shipment.shipmentDate}</div>
                                        </div>
                                        <div className="text-end">
                                            <div>Qty: {shipment.totalQty}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Payments */}
                    {order.payments && order.payments.length > 0 && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Payments</h5>
                            </div>
                            <div className="card-body">
                                {order.payments.map((payment) => (
                                    <div key={payment.orderPaymentId} className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <div>Method: {payment.method}</div>
                                            <div className="text-muted small">{payment.paymentDate}</div>
                                        </div>
                                        <div className="text-end">
                                            <div className="text-success">{OrderService.formatCurrency(payment.amountPaid)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Status Change Modal */}
            {showStatusModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Change Order Status</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={closeStatusModal}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Order: {order.orderNo}</label>
                                    <p className="text-muted">Current Status: {order.status}</p>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">New Status</label>
                                    <select
                                        className="form-select"
                                        value={newStatus}
                                        onChange={(e) => setNewStatus(e.target.value)}
                                    >
                                        {OrderService.getOrderStatusOptions().map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Remarks (Optional)</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={statusRemarks}
                                        onChange={(e) => setStatusRemarks(e.target.value)}
                                        placeholder="Enter any remarks about this status change..."
                                    ></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={closeStatusModal}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={handleStatusChange}
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Updating...
                                        </>
                                    ) : (
                                        'Update Status'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default OrderDetails

// Add CSS styles for address formatting and mobile responsiveness
const styles = `
    /* Address formatting styles similar to InvoiceCreate.js */
    .address-details {
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 1rem;
        transition: all 0.2s ease;
    }

    .address-details:hover {
        border-color: #3b82f6;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
    }

    .address-line {
        font-size: 13px;
        color: #4b5563;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: flex-start;
        line-height: 1.4;
    }

    .address-line:last-child {
        margin-bottom: 0;
    }

    .address-line i {
        margin-top: 0.125rem;
        flex-shrink: 0;
    }

    .address-line strong {
        color: #374151;
        font-weight: 600;
    }

    /* Mobile responsive adjustments */
    @media (max-width: 768px) {
        .card-body {
            padding: 1rem;
        }

        .address-details {
            padding: 0.75rem;
        }

        .address-line {
            font-size: 12px;
            margin-bottom: 0.375rem;
        }

        .card-header {
            padding: 0.75rem 1rem;
        }

        .card-header h5 {
            font-size: 1rem;
        }
    }

    @media (max-width: 576px) {
        .card-body {
            padding: 0.75rem;
        }

        .address-details {
            padding: 0.5rem;
        }

        .address-line {
            font-size: 11px;
            margin-bottom: 0.25rem;
        }

        .card-header {
            padding: 0.5rem 0.75rem;
        }

        .card-header h5 {
            font-size: 0.9rem;
        }
    }
`;

// Inject styles into the document
if (typeof document !== 'undefined') {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
} 